import React, { useEffect, useState } from "react";
import MainLayout from "../../../layouts/main/main_layout";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import XLSIcon from "../../../assets/xls_icon.svg";
import { ArrowBackRounded, MiscellaneousServicesRounded, StarRounded } from "@mui/icons-material";
import Loading from "../../../components/loading/loading";
import { ServiceDetail } from "../model/service_details_model";
import { useNavigate, useParams } from "react-router-dom";
import {
  deleteService,
  downloadServiceDetails,
  getServiceDetails,
  updateService,
} from "../../../services/services/services";
import { useQuery } from "react-query";
import StatusMenu from "../components/status_menu/status_menu";
import UpdateServiceStatusToReject from "../components/update_service_status/update_service_status_popup";
import UpdateServiceStatusToDelete from "../components/update_service_status/update_service_status_popup";
import { useFeedback } from "../../../providers/feedback/feeedback";
import { queryClient } from "../../../providers/query/query";
import { downloadFromBlob } from "../../../utils/utils";
import useUser from "../../../providers/user/user_provider";
import { ToolbarButton } from "../../../components/toolbar_button/toolbar_button";
import { black, greyEnabled, purple, purpleLight } from "../../../theme/colors";
import ServiceImageGallery from "./components/service_image_gallery";

type SubmittingAction = "approve" | "reject" | "delete" | "status" | null;

export default function ServiceDetails() {
  const [serviceStatus, setServiceStatus] = useState<string | null>(null);
  const [closeRejectServiceStatusDialog, setCloseRejectServiceStatusDialog] =
    useState<boolean>(false);
  const [closeDeleteServiceStatusDialog, setCloseDeleteServiceStatusDialog] =
    useState<boolean>(false);
  const [submittingAction, setSubmittingAction] = useState<SubmittingAction>(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const isSubmitting = submittingAction !== null;

  let { id } = useParams();
  const navigate = useNavigate();
  const { showSnackbar } = useFeedback();
  const { hasPermission } = useUser();

  const { data: serviceDetails, isLoading } = useQuery<ServiceDetail>(
    ["service-details", id],
    async () => {
      let data = await getServiceDetails({ serviceId: +id! });
      return data?.data?.data?.data;
    }
  );

  function invalidateServiceQueries() {
    queryClient.invalidateQueries({ queryKey: ["service-details"] });
    queryClient.invalidateQueries({ queryKey: ["services"] });
  }

  // Shared by the reject flow and the status-menu flow — both just update
  // the service's status/comment and refresh the detail + list queries.
  function updateServiceStatus(comments: string, statusId: number, action: SubmittingAction) {
    setSubmittingAction(action);
    updateService({
      comment: comments,
      statusId: statusId,
      serviceId: +id!,
    })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Service status updated successfully");
          invalidateServiceQueries();
          if (action === "reject") {
            setCloseRejectServiceStatusDialog(false);
          }
        }
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            "Failed to update the service status. Please try again."
        );
      })
      .finally(() => setSubmittingAction(null));
  }

  function deleteServiceApiCall(comments: string) {
    setSubmittingAction("delete");
    deleteService({ serviceId: +id!, comment: comments })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Service removed successfully");
          setCloseDeleteServiceStatusDialog(false);
          queryClient.invalidateQueries({ queryKey: ["services"] });
          navigate(-1);
        } else {
          showSnackbar("Something went wrong while removing the service.");
        }
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            "Failed to delete the service. Please try again."
        );
      })
      .finally(() => setSubmittingAction(null));
  }

  function approveService() {
    setSubmittingAction("approve");
    updateService({
      comment: "",
      statusId: 3,
      serviceId: +id!,
    })
      .then((res) => {
        if (res.status === 200) {
          showSnackbar("Service status updated successfully");
          invalidateServiceQueries();
        }
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            "Failed to approve the service. Please try again."
        );
      })
      .finally(() => setSubmittingAction(null));
  }

  function downloadExcel() {
    setIsDownloading(true);
    downloadServiceDetails(id!, "excel")
      .then((response) => {
        downloadFromBlob(response, new Date().valueOf() + ".xlsx");
      })
      .catch((e) => {
        showSnackbar(
          e?.response?.data?.error?.message ||
            "Failed to generate the report. Please try again."
        );
      })
      .finally(() => setIsDownloading(false));
  }

  function listItem({
    title,
    value,
  }: {
    title: string;
    value: any;
  }): JSX.Element {
    return (
      <Box>
        <Typography variant="body2" color={greyEnabled}>
          {title}
        </Typography>
        <Typography color={black} fontWeight={600}>
          {value || "-"}
        </Typography>
      </Box>
    );
  }
  function loading() {
    return (
      <Box
        height={500}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        <Loading />
      </Box>
    );
  }

  useEffect(() => {
    setServiceStatus(serviceDetails?.status!);
  }, [serviceDetails]);

  return (
    <MainLayout>
      <Box display="flex" alignItems="center" gap={1}>
        <IconButton onClick={() => navigate(-1)}>
          <ArrowBackRounded />
        </IconButton>
        <Box display="flex" alignItems="center" gap={1.5}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 40,
              height: 40,
              borderRadius: 2,
              bgcolor: purpleLight,
              flexShrink: 0,
            }}
          >
            <MiscellaneousServicesRounded sx={{ color: purple, fontSize: 22 }} />
          </Box>
          <Box>
            <Typography variant="h5" fontWeight={700} color={black}>
              Service Details
            </Typography>
            <Typography variant="body2" color={greyEnabled}>
              Review and manage this service listing.
            </Typography>
          </Box>
        </Box>
      </Box>

      <Grid container spacing={3} mt={0.5}>
        <Grid item md={8} lg={7} sm={12} xs={12}>
          <Card
            elevation={0}
            sx={{ width: "100%", borderRadius: 3, border: "1px solid #ECECEC" }}
          >
            <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
              {isLoading ? (
                loading()
              ) : (
                <Box overflow="auto" flex={1}>
                  <Grid container spacing={3}>
                    <Grid item md={6}>
                      {listItem({
                        title: "Sl. No.",
                        value: +id!,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Name of Service / Person",
                        value: serviceDetails?.name,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Surveyor Name",
                        value: serviceDetails?.surveyorName,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Service description",
                        value: serviceDetails?.description,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Service Category",
                        value: serviceDetails?.category,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Service Sub Category",
                        value: serviceDetails?.subCategory,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "State",
                        value: serviceDetails?.state,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "City",
                        value: serviceDetails?.city,
                      })}
                    </Grid>

                    <Grid item md={6}>
                      {listItem({
                        title: "District",
                        value: serviceDetails?.district,
                      })}
                    </Grid>

                    <Grid item md={6}>
                      {listItem({
                        title: "Service Contact Number",
                        value: `+91 ${serviceDetails?.mobileNumber}`,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      {listItem({
                        title: "Service Email ID",
                        value: serviceDetails?.serviceEmailId,
                      })}
                    </Grid>
                    {/* <Grid item md={6}>
                      {listItem({
                        title: "Service Website URL",
                        value: serviceDetails?.websiteURL,
                      })}
                    </Grid> */}
                    <Grid item md={6}>
                      {listItem({
                        title: "Whatsapp Number",
                        value: `+91 ${serviceDetails?.serviceMobileNo}`,
                      })}
                    </Grid>
                    <Grid item md={6}>
                      <Box>
                        <StatusMenu
                          status={serviceDetails?.status || ""}
                          updatedStatus={serviceStatus}
                          disabled={isSubmitting}
                          onStatusChange={(status) => {
                            if (status !== serviceStatus) {
                              setServiceStatus(status);
                              if (status === "Modified") {
                                updateServiceStatus("", 5, "status");
                              } else {
                                updateServiceStatus("", 2, "status");
                              }
                            }
                          }}
                        />
                      </Box>
                    </Grid>
                    {serviceDetails?.reviewCount ||
                      serviceDetails?.overallRating ? (
                      <Grid item md={6}>
                        <Box>
                          <Typography variant="body2" color={greyEnabled}>
                            {"Review and Ratings"}
                          </Typography>
                          <Box
                            display="flex"
                            flexDirection={"row"}
                            sx={{
                              backgroundColor: purpleLight,
                              width: "fit-content",
                              borderRadius: 3,
                            }}
                            px={2}
                            py={1}
                          >
                            <StarRounded
                              sx={{ color: "#F3B820", height: 17, width: 17 }}
                            />
                            <Typography variant="caption" fontWeight="600">
                              {serviceDetails?.overallRating} |
                            </Typography>
                            <Typography variant="caption" ml={0.5}>
                              {serviceDetails?.reviewCount}
                              {" reviews"}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    ) : null}
                  </Grid>
                  {serviceDetails?.systemComments && (
                    <>
                      <Box mt={2} mb={2}>
                        <Divider />
                      </Box>
                      <Grid item md={12}>
                        {listItem({
                          title: "Reason for Rejection",
                          value: serviceDetails?.systemComments,
                        })}
                      </Grid>
                    </>
                  )}

                  <ServiceImageGallery title="Uploaded images" images={serviceDetails?.documents} />
                  <ServiceImageGallery title="Uploaded Aadhar images" images={serviceDetails?.aadharImages} />
                  <Grid container>
                    {serviceDetails?.documents?.map((doc) => {
                      return <Grid item md={3}></Grid>;
                    })}
                  </Grid>
                  <Box mt={2} mb={2}>
                    <Divider />
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Typography color={black}>Generate Report:</Typography>
                    <Box ml={2}>
                      <ToolbarButton
                        icon={XLSIcon}
                        label="Export"
                        loading={isDownloading}
                        onClick={downloadExcel}
                      />
                    </Box>
                  </Box>
                  <Grid item md={12}>
                    <Box display="flex" justifyContent="space-between">
                      <Button
                        variant="outlined"
                        disabled={isSubmitting}
                        sx={{
                          pl: 2,
                          pr: 2,
                          borderColor: purple,
                          color: purple,
                          borderRadius: 2,
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": { borderColor: purple, bgcolor: purpleLight },
                        }}
                        onClick={() => navigate(-1)}
                      >
                        Back To Services
                      </Button>
                      <Box display="flex" alignItems="center">
                        {serviceDetails?.status === "Rejected" ||
                          serviceDetails?.status === "Approved" || !hasPermission("services", "update") ? null : (
                          <Button
                            variant="outlined"
                            disabled={isSubmitting}
                            sx={{
                              pl: 4,
                              pr: 4,
                              borderColor: purple,
                              color: purple,
                              borderRadius: 2,
                              textTransform: "none",
                              fontWeight: 600,
                              "&:hover": { borderColor: purple, bgcolor: purpleLight },
                            }}
                            onClick={() => {
                              setCloseRejectServiceStatusDialog(true);
                            }}
                          >
                            Reject Listing
                          </Button>
                        )}
                        {serviceDetails?.status === "Rejected" &&
                          <Chip
                            label="Rejected"
                            variant="outlined"
                            sx={{
                              minWidth: 150,
                              ml: 2,
                              borderColor: purple,
                              color: purple,
                              fontWeight: 600,
                              borderRadius: 2,
                            }}
                          />
                        }

                        {hasPermission("services", "update") &&
                          (serviceDetails?.status === 'Modified' ||
                            serviceDetails?.status === 'In Progress' ||
                            serviceDetails?.status === 'Pending') &&
                          <Button
                            variant="contained"
                            disableElevation
                            disabled={isSubmitting}
                            startIcon={
                              submittingAction === "approve" ? (
                                <CircularProgress size={16} sx={{ color: "#fff" }} />
                              ) : undefined
                            }
                            sx={{
                              pl: 4,
                              pr: 4,
                              fontWeight: 600,
                              ml: 2,
                              bgcolor: purple,
                              borderRadius: 2,
                              textTransform: "none",
                              "&:hover": { bgcolor: "#7A2074" },
                            }}
                            onClick={() => {
                              approveService();
                            }}
                          >
                            Approve Listing
                          </Button>
                        }
                        {hasPermission("services", "delete") && serviceDetails?.status === 'Approved' &&
                          <Button
                            variant="contained"
                            disableElevation
                            disabled={isSubmitting}
                            sx={{
                              pl: 4,
                              pr: 4,
                              fontWeight: 600,
                              ml: 2,
                              bgcolor: purple,
                              borderRadius: 2,
                              textTransform: "none",
                              "&:hover": { bgcolor: "#7A2074" },
                            }}
                            onClick={() => {
                              setCloseDeleteServiceStatusDialog(true);
                            }}
                          >
                            {"Delete Listing"}
                          </Button>
                        }
                      </Box>
                    </Box>
                  </Grid>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {closeRejectServiceStatusDialog && (
        <UpdateServiceStatusToReject
          open={closeRejectServiceStatusDialog}
          submitting={submittingAction === "reject"}
          onDialogClose={() => {
            setCloseRejectServiceStatusDialog(false);
          }}
          updateServiceStatus={(comments) => {
            updateServiceStatus(comments, 4, "reject");
          }}
          title={"Reject Service"}
          subtitle={"Reason for rejection:"}
        />
      )}
      {closeDeleteServiceStatusDialog && (
        <UpdateServiceStatusToDelete
          open={closeDeleteServiceStatusDialog}
          submitting={submittingAction === "delete"}
          onDialogClose={() => {
            setCloseDeleteServiceStatusDialog(false);
          }}
          updateServiceStatus={(comments) => {
            deleteServiceApiCall(comments);
          }}
          title={"Delete Service"}
          subtitle={"Reason for Deletion:"}
        />
      )}
    </MainLayout>
  );
}
